/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import {
    Carousel,
    CarouselControl,
    CarouselIndicators,
    CarouselItem
} from "reactstrap";
import * as styles from "./imageManagement.styles";

interface ImageCarouselProps {
    images: any[];
    onDeleteImage: (image: any) => void;
    activeIndex: number;
    onIndexChange: (index: number) => void;
}

export function ImageCarousel({ images, onDeleteImage, activeIndex, onIndexChange }: ImageCarouselProps) {
    const [animating, setAnimating] = useState(false);

    if (!images || images.length === 0) {
        return null;
    }

    const next = () => {
        if (animating) return;
        const nextIndex = activeIndex === images.length - 1 ? 0 : activeIndex + 1;
        onIndexChange(nextIndex);
    };

    const previous = () => {
        if (animating) return;
        const nextIndex = activeIndex === 0 ? images.length - 1 : activeIndex - 1;
        onIndexChange(nextIndex);
    };

    const goToIndex = (newIndex: number) => {
        if (animating) return;
        onIndexChange(newIndex);
    };

    const handleDelete = (image: any) => {
        onDeleteImage(image);
        // If we delete the last item, go to previous
        if (activeIndex >= images.length - 1 && activeIndex > 0) {
            onIndexChange(activeIndex - 1);
        }
    };

    const slides = images.map((image, index) => {
        return (
            <CarouselItem
                onExiting={() => setAnimating(true)}
                onExited={() => setAnimating(false)}
                key={image.id || index}
            >
                <div
                    css={css`
                        position: relative;
                        width: 100%;
                        max-width: 800px;
                        margin: 0 auto;
                        padding: 2rem 0;
                    `}
                >
                    <div
                        css={css`
                            position: relative;
                            width: 100%;
                            background: #f5f5f5;
                            border-radius: 8px;
                            overflow: hidden;
                        `}
                    >
                        <img
                            src={image.data_url}
                            alt={`Image ${index + 1}`}
                            css={css`
                                width: 100%;
                                height: auto;
                                max-height: 600px;
                                object-fit: contain;
                                display: block;
                            `}
                        />
                        <button
                            css={css`
                                ${styles.closeButtonStyles}
                                top: 1rem;
                                right: 1rem;
                                transform: none;
                                z-index: 10;
                            `}
                            onClick={() => handleDelete(image)}
                            title="Delete image"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    {images.length > 1 && (
                        <div
                            css={css`
                                text-align: center;
                                margin-top: 1rem;
                                color: #666;
                                font-size: 0.9rem;
                            `}
                        >
                            Image {activeIndex + 1} of {images.length}
                        </div>
                    )}
                </div>
            </CarouselItem>
        );
    });

    return (
        <div className="mt-4">
            <Carousel
                activeIndex={activeIndex}
                next={next}
                previous={previous}
                interval={false}
            >
                <CarouselIndicators
                    items={images.map((img, idx) => ({ key: idx }))}
                    activeIndex={activeIndex}
                    onClickHandler={goToIndex}
                />
                {slides}
                {images.length > 1 && (
                    <>
                        <CarouselControl
                            direction="prev"
                            directionText="Previous"
                            onClickHandler={previous}
                        />
                        <CarouselControl
                            direction="next"
                            directionText="Next"
                            onClickHandler={next}
                        />
                    </>
                )}
            </Carousel>
        </div>
    );
}

